export type SettingsNavLink = {
  href: string;
  label: string;
  implemented?: boolean;
};

export type SettingsNavGroup = {
  id: string;
  label: string;
  expandable: true;
  items: SettingsNavLink[];
};

export type SettingsNavEntry = SettingsNavLink | SettingsNavGroup;

export type SettingsNavSection = {
  id: string;
  label: string;
  items: SettingsNavEntry[];
};

export function isSettingsNavGroup(entry: SettingsNavEntry): entry is SettingsNavGroup {
  return "expandable" in entry && entry.expandable === true;
}

export const SETTINGS_GET_STARTED: SettingsNavLink = {
  href: "/settings/getting-started",
  label: "Get started",
  implemented: true,
};

export const SETTINGS_NAV_SECTIONS: SettingsNavSection[] = [
  {
    id: "personal",
    label: "Personal",
    items: [
      { href: "/settings/account", label: "Profile", implemented: true },
    ],
  },
  {
    id: "workspace",
    label: "Workspace",
    items: [
      { href: "/settings/workspace-overview", label: "Workspace", implemented: true },
      { href: "/settings/users", label: "Users", implemented: true },
      { href: "/settings/companies", label: "Companies", implemented: true },
      { href: "/settings/connections", label: "Connections", implemented: true },
      { href: "/settings/observability", label: "Observability", implemented: true },
      { href: "/settings/ai", label: "AI providers", implemented: true },
    ],
  },
  {
    id: "help",
    label: "Help",
    items: [
      // Docs live at the root /docs section, served from CMS content.
      { href: "/docs", label: "Docs", implemented: true },
    ],
  },
];

const EXPANDED_KEY = "ceo-cmd.settings.nav.expanded";

export function flattenSettingsLinks(): SettingsNavLink[] {
  const out: SettingsNavLink[] = [SETTINGS_GET_STARTED];
  for (const section of SETTINGS_NAV_SECTIONS) {
    for (const entry of section.items) {
      if (isSettingsNavGroup(entry)) out.push(...entry.items);
      else out.push(entry);
    }
  }
  return out;
}

export function isSettingsActivePath(href: string, pathname: string): boolean {
  if (href === "/settings/account") {
    return pathname === "/settings/account" || pathname === "/settings/profile";
  }
  if (href === "/settings/workspace-overview") {
    return (
      pathname === "/settings/workspace-overview" ||
      pathname === "/settings/workspace"
    );
  }
  if (href === "/settings/users") {
    return pathname === "/settings/users";
  }
  if (href === "/settings/companies") {
    return pathname === "/settings/companies";
  }
  if (href === "/settings/ai") {
    return pathname === "/settings/ai";
  }
  if (href === "/settings/connections") {
    return pathname === "/settings/connections";
  }
  if (href === "/settings/observability") {
    return pathname === "/settings/observability";
  }
  if (href === "/docs") {
    return pathname === "/docs" || pathname.startsWith("/docs/");
  }
  if (pathname === href) return true;
  if (pathname.startsWith(`${href}/`)) {
    return href.split("/").length < pathname.split("/").length;
  }
  return false;
}

export function groupContainsPath(group: SettingsNavGroup, pathname: string): boolean {
  return group.items.some((item) => isSettingsActivePath(item.href, pathname));
}

export function groupIdsForPath(pathname: string): string[] {
  const ids: string[] = [];
  for (const section of SETTINGS_NAV_SECTIONS) {
    for (const entry of section.items) {
      if (isSettingsNavGroup(entry) && groupContainsPath(entry, pathname)) {
        ids.push(entry.id);
      }
    }
  }
  return ids;
}

export function loadExpandedSettingsGroups(pathname: string): Set<string> {
  let stored: string[] = [];
  try {
    const raw = localStorage.getItem(EXPANDED_KEY);
    if (raw) stored = JSON.parse(raw) as string[];
  } catch {
    /* ignore */
  }
  const next = new Set(stored);
  for (const id of groupIdsForPath(pathname)) next.add(id);
  return next;
}

export function persistExpandedSettingsGroups(ids: Set<string>): void {
  try {
    localStorage.setItem(EXPANDED_KEY, JSON.stringify([...ids]));
  } catch {
    /* ignore */
  }
}

export function filterSettingsSections(query: string): SettingsNavSection[] {
  const q = query.trim().toLowerCase();
  if (!q) return SETTINGS_NAV_SECTIONS;

  return SETTINGS_NAV_SECTIONS.map((section) => {
    const items: SettingsNavEntry[] = [];
    for (const entry of section.items) {
      if (isSettingsNavGroup(entry)) {
        const matchedChildren = entry.items.filter((i) =>
          i.label.toLowerCase().includes(q),
        );
        if (entry.label.toLowerCase().includes(q)) {
          items.push(entry);
        } else if (matchedChildren.length > 0) {
          items.push({ ...entry, items: matchedChildren });
        }
      } else if (entry.label.toLowerCase().includes(q)) {
        items.push(entry);
      }
    }
    return { ...section, items };
  }).filter((s) => s.items.length > 0);
}
